import React, { useState, useEffect } from 'react'
import {
  FiX,
  FiTrendingUp,
  FiCheckCircle,
  FiPackage,
  FiMapPin,
  FiFileText,
  FiGrid,
  FiMail,
  FiPlay,
} from 'react-icons/fi'

export type GisaCommandActionType =
  | 'sellout'
  | 'regularidade'
  | 'estoque'
  | 'pdvs'
  | 'pdf'
  | 'excel'
  | 'email'
  | null

interface CommandConfigModalProps {
  isOpen: boolean
  onClose: () => void
  action: GisaCommandActionType
  onExecute: (query: string, directTask?: 'pdf' | 'excel' | 'email', taskOptions?: any) => void
}

const PERIOD_OPTIONS = [
  { value: 'mes_atual', label: 'Mês Atual (Por Mês)' },
  { value: '3_meses', label: 'Últimos 3 Meses (Trimestral)' },
  { value: '6_meses', label: 'Últimos 6 Meses (Semestral)' },
  { value: 'anual', label: 'Anual (12 Meses Acumulado)' },
]

export function CommandConfigModal({
  isOpen,
  onClose,
  action,
  onExecute,
}: CommandConfigModalProps) {
  const [period, setPeriod] = useState('anual')
  const [groupBy, setGroupBy] = useState<'distribuidor' | 'mensal' | 'uf'>('distribuidor')
  const [reportScope, setReportScope] = useState('Resumo Executivo Geral (Sellout & Faturamento)')
  const [emailTo, setEmailTo] = useState('')
  const [emailFormat, setEmailFormat] = useState<'PDF' | 'EXCEL' | 'CSV'>('PDF')

  useEffect(() => {
    if (isOpen) {
      setPeriod('anual')
      setGroupBy('distribuidor')
      setReportScope('Resumo Executivo Geral (Sellout & Faturamento)')
    }
  }, [isOpen, action])

  if (!isOpen || !action) return null

  const getHeaderMeta = () => {
    switch (action) {
      case 'sellout':
        return {
          title: 'Análise de Sellout & Faturamento',
          subtitle: 'Configure o recorte para a GID IA gerar o diagnóstico e gráfico de vendas',
          icon: <FiTrendingUp size={20} color="#0284c7" />,
          color: '#0284c7',
        }
      case 'regularidade':
        return {
          title: 'Auditoria de Regularidade de Envios',
          subtitle: 'Verificar distribuidores em dia versus pendentes de envio (> 7 dias)',
          icon: <FiCheckCircle size={20} color="#8b5cf6" />,
          color: '#8b5cf6',
        }
      case 'estoque':
        return {
          title: 'Diagnóstico de Estoque & Giro',
          subtitle: 'Analisar posição de estoque por distribuidor, cobertura e alertas',
          icon: <FiPackage size={20} color="#f97316" />,
          color: '#f97316',
        }
      case 'pdvs':
        return {
          title: 'Cobertura de PDVs & Clientes Ativos',
          subtitle: 'Cruzar carteira de clientes, giro de PDVs e potencial territorial IBGE',
          icon: <FiMapPin size={20} color="#10b981" />,
          color: '#10b981',
        }
      case 'pdf':
        return {
          title: 'Gerar Relatório Executivo em PDF',
          subtitle: 'Compilar indicadores do GID em documento PDF corporativo imediato',
          icon: <FiFileText size={20} color="#ef4444" />,
          color: '#ef4444',
        }
      case 'excel':
        return {
          title: 'Exportar Planilha Excel (.xlsx)',
          subtitle: 'Baixar pasta de trabalho multi-abas com indicadores, distribuidores e produtos',
          icon: <FiGrid size={20} color="#16a34a" />,
          color: '#16a34a',
        }
      case 'email':
        return {
          title: 'Disparar Relatório GID por E-mail Agora',
          subtitle: 'Enviar boletim executivo com anexo para o e-mail desejado',
          icon: <FiMail size={20} color="#eab308" />,
          color: '#eab308',
        }
      default:
        return {
          title: 'Configurar Comando GID IA',
          subtitle: '',
          icon: null,
          color: '#2563eb',
        }
    }
  }

  const meta = getHeaderMeta()

  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault()
    const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label || period

    if (action === 'pdf') {
      onExecute(`Gerar PDF Executivo (${reportScope}) - ${periodLabel}`, 'pdf', {
        period,
        reportScope,
      })
      onClose()
      return
    }

    if (action === 'excel') {
      onExecute(`Exportar Planilha Excel (${reportScope}) - ${periodLabel}`, 'excel', {
        period,
        reportScope,
      })
      onClose()
      return
    }

    if (action === 'email') {
      onExecute(
        `Disparar relatório executivo por e-mail (${emailFormat}) no período ${periodLabel}`,
        'email',
        {
          period,
          emailTo: emailTo.trim(),
          emailFormat,
        }
      )
      onClose()
      return
    }

    let prompt = ''
    if (action === 'sellout') {
      prompt =
        groupBy === 'mensal'
          ? `Analise a evolução mensal de Sellout e faturamento (R$ e volume) no recorte ${periodLabel} e mostre o gráfico.`
          : `Mostre o ranking de Sellout e faturamento por distribuidor no período ${periodLabel} com análise executiva.`
    } else if (action === 'regularidade') {
      prompt = `Faça uma auditoria completa de regularidade de envios dos distribuidores ativos, destacando quem está em dia e quem está pendente há mais de 7 dias.`
    } else if (action === 'estoque') {
      prompt = `Apresente o diagnóstico da posição de estoque por distribuidor (${periodLabel}), cobertura de SKUs e produtos inválidos pendentes.`
    } else if (action === 'pdvs') {
      prompt = `Analise a cobertura de PDVs, carteira de clientes ativos no GID e dê recomendações para acompanhamento de giro.`
    }

    onExecute(prompt)
    onClose()
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100050,
        background: 'rgba(2, 6, 23, 0.72)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 460,
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: 16,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
          overflow: 'hidden',
          color: '#f8fafc',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: `${meta.color}20`,
                border: `1px solid ${meta.color}45`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {meta.icon}
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#f8fafc' }}>
                {meta.title}
              </h4>
              <p style={{ margin: '2px 0 0', fontSize: 11.5, color: '#94a3b8' }}>
                {meta.subtitle}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleConfirm} style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 11.5,
                fontWeight: 700,
                color: '#cbd5e1',
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Janela de Período
            </label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                background: '#1e293b',
                border: '1px solid #334155',
                color: '#f8fafc',
                fontSize: 13,
                outline: 'none',
              }}
            >
              {PERIOD_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          {action === 'sellout' && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: '#cbd5e1',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Visão do Gráfico Interativo
              </label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as any)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: '#1e293b',
                  border: '1px solid #334155',
                  color: '#f8fafc',
                  fontSize: 13,
                  outline: 'none',
                }}
              >
                <option value="distribuidor">Ranking por Distribuidor (Top Sellout R$)</option>
                <option value="mensal">Evolução Mensal (Série Histórica)</option>
              </select>
            </div>
          )}

          {(action === 'pdf' || action === 'excel') && (
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: '#cbd5e1',
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Escopo do Relatório
              </label>
              <select
                value={reportScope}
                onChange={(e) => setReportScope(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: '#1e293b',
                  border: '1px solid #334155',
                  color: '#f8fafc',
                  fontSize: 13,
                  outline: 'none',
                }}
              >
                <option value="Resumo Executivo Geral (Sellout & Faturamento)">
                  Resumo Executivo Geral (Sellout & Faturamento)
                </option>
                <option value="Levantamento Cadastral de Distribuidores (Nome, UF e Telefone)">
                  Levantamento Cadastral de Distribuidores (Nome, UF e Telefone)
                </option>
                <option value="Auditoria de Regularidade de Envios (7 Dias)">
                  Auditoria de Regularidade de Envios (7 Dias)
                </option>
                <option value="Posição de Estoque e Catálogo de Produtos">
                  Posição de Estoque e Catálogo de Produtos
                </option>
              </select>
            </div>
          )}

          {action === 'email' && (
            <>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: '#cbd5e1',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  E-mail de Destino (Opcional — usa o e-mail agendado se vazio)
                </label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="exemplo@empresa.com.br"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: '#1e293b',
                    border: '1px solid #334155',
                    color: '#f8fafc',
                    fontSize: 13,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: '#cbd5e1',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Formato do Anexo
                </label>
                <select
                  value={emailFormat}
                  onChange={(e) => setEmailFormat(e.target.value as any)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: '#1e293b',
                    border: '1px solid #334155',
                    color: '#f8fafc',
                    fontSize: 13,
                    outline: 'none',
                  }}
                >
                  <option value="PDF">Documento Executivo PDF (.pdf)</option>
                  <option value="EXCEL">Planilha Corporativa Excel (.xls)</option>
                  <option value="CSV">Arquivo de Dados (.csv)</option>
                </select>
              </div>
            </>
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              marginTop: 6,
              paddingTop: 12,
              borderTop: '1px solid #1e293b',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                background: 'transparent',
                border: '1px solid #334155',
                color: '#cbd5e1',
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                background: meta.color || '#2563eb',
                border: 'none',
                color: '#ffffff',
                fontSize: 12.5,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FiPlay size={13} /> Executar com a GISA
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

